<script lang="ts">
	import { page } from '$app/stores';
	import { handleTabKeydown } from '$lib/utils/tabs';

	let lang = $derived($page.params.lang || 'en');

	let activeTab = $state<'curl' | 'python' | 'typescript'>('curl');

	const curlExample = `curl -X GET "https://api.ameta.no/v1/assets/pump-001/health" \\
  -H "Authorization: Bearer YOUR_API_KEY"`;

	const curlResponse = `{
  "data": {
    "asset_id": "pump-001",
    "score": 87.5,
    "status": "healthy",
    "remaining_useful_life": {
      "estimated_days": 142,
      "confidence": "high"
    },
    "anomalies": []
  }
}`;

	// pythonExample and typescriptExample are rendered inline in the template with syntax highlighting spans
	// so these string constants are kept only for reference/maintenance.
	const _pythonExample = `from ameta import AmetaClient

client = AmetaClient(api_key="YOUR_API_KEY")

# Get asset health score
health = client.predictions.get_health("pump-001")
print(f"Health: {health.score}/100")
print(f"Days until maintenance: {health.rul.estimated_days}")

# Stream real-time readings
for reading in client.metrics.stream("temperature-001"):
    if reading.value > 80:
        print(f"Warning: {reading.value}°C")`;

	const _typescriptExample = `import { AmetaClient } from '@ameta/sdk';

const client = new AmetaClient({
  apiKey: process.env.AMETA_API_KEY
});

// Get asset health
const health = await client.predictions.getHealth('pump-001');
console.log(\`Health: \${health.score}/100\`);

// Subscribe to alerts
client.alerts.subscribe('pump-001', (alert) => {
  console.log(\`Alert: \${alert.message}\`);
  slack.notify(alert);
});`;

	const tabs = [
		{ id: 'curl' as const, label: 'cURL' },
		{ id: 'python' as const, label: 'Python' },
		{ id: 'typescript' as const, label: 'TypeScript' }
	];
</script>

<div
	class="api-showcase rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl w-full max-w-full"
>
	<!-- Tabs -->
	<div
		class="api-tabs flex border-b border-slate-800"
		role="tablist"
		tabindex="0"
		onkeydown={(e: KeyboardEvent) => {
			const next = handleTabKeydown(
				e,
				tabs.map((t) => t.id),
				activeTab
			);
			if (next) activeTab = next;
		}}
	>
		{#each tabs as tab}
			<button
				class="flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-all duration-200 {activeTab ===
				tab.id
					? 'text-ameta-green bg-slate-800/50 border-b-2 border-ameta-green'
					: 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'}"
				onclick={() => (activeTab = tab.id)}
				role="tab"
				aria-selected={activeTab === tab.id}
				tabindex={activeTab === tab.id ? 0 : -1}
			>
				{tab.label}
			</button>
		{/each}
	</div>

	<!-- Code content -->
	<div class="p-3 sm:p-6" role="tabpanel" aria-label={tabs.find((t) => t.id === activeTab)?.label}>
		{#if activeTab === 'curl'}
			<div class="space-y-3 sm:space-y-4">
				<div>
					<p class="text-[10px] sm:text-xs text-slate-500 mb-2 uppercase tracking-wider">Request</p>
					<pre
						class="text-[11px] sm:text-sm font-mono text-slate-300 bg-slate-950 rounded-lg p-3 sm:p-4 overflow-x-auto"><code
							>{curlExample}</code
						></pre>
				</div>
				<div>
					<p class="text-[10px] sm:text-xs text-slate-500 mb-2 uppercase tracking-wider">
						Response
					</p>
					<pre
						class="text-[11px] sm:text-sm font-mono bg-slate-950 rounded-lg p-3 sm:p-4 overflow-x-auto"><code
							class="text-slate-300">{curlResponse}</code
						></pre>
				</div>
			</div>
		{:else if activeTab === 'python'}
			<pre
				class="text-[11px] sm:text-sm font-mono bg-slate-950 rounded-lg p-3 sm:p-4 overflow-x-auto"><code
					><span class="text-pink-400">from</span> <span class="text-slate-300">ameta</span> <span
						class="text-pink-400">import</span
					> <span class="text-slate-300">AmetaClient</span>

<span class="text-slate-300">client</span> <span class="text-pink-400">=</span> <span
						class="text-slate-300">AmetaClient</span
					><span class="text-slate-400">(</span><span class="text-slate-300">api_key</span><span
						class="text-pink-400">=</span
					><span class="text-green-400">"YOUR_API_KEY"</span><span class="text-slate-400">)</span>

<span class="text-slate-500"># Get asset health score</span>
<span class="text-slate-300">health</span> <span class="text-pink-400">=</span> <span
						class="text-slate-300">client.predictions.get_health</span
					><span class="text-slate-400">(</span><span class="text-green-400">"pump-001"</span><span
						class="text-slate-400">)</span
					>
<span class="text-cyan-400">print</span><span class="text-slate-400">(</span><span
						class="text-green-400"
						>f"Health: </span><span class="text-slate-400">{'{'}</span><span class="text-slate-300"
						>health.score</span
					><span class="text-slate-400">{'}'}</span><span class="text-green-400">/100"</span><span
						class="text-slate-400">)</span
					>
<span class="text-cyan-400">print</span><span class="text-slate-400">(</span><span
						class="text-green-400"
						>f"Days until maintenance: </span><span class="text-slate-400">{'{'}</span><span
						class="text-slate-300">health.rul.estimated_days</span
					><span class="text-slate-400">{'}'}</span><span class="text-green-400">"</span><span
						class="text-slate-400">)</span
					>

<span class="text-slate-500"># Stream real-time readings</span>
<span class="text-pink-400">for</span> <span class="text-slate-300">reading</span> <span
						class="text-pink-400">in</span
					> <span class="text-slate-300">client.metrics.stream</span><span class="text-slate-400"
						>(</span
					><span class="text-green-400">"temperature-001"</span><span class="text-slate-400">)</span
					><span class="text-pink-400">:</span>
    <span class="text-pink-400">if</span> <span class="text-slate-300">reading.value</span> <span
						class="text-pink-400">></span
					> <span class="text-amber-400">80</span><span class="text-pink-400">:</span>
        <span class="text-cyan-400">print</span><span class="text-slate-400">(</span><span
						class="text-green-400"
						>f"Warning: </span><span class="text-slate-400">{'{'}</span><span class="text-slate-300"
						>reading.value</span
					><span class="text-slate-400">{'}'}</span><span class="text-green-400">°C"</span><span
						class="text-slate-400">)</span
					></code
				></pre>
		{:else if activeTab === 'typescript'}
			<pre
				class="text-[11px] sm:text-sm font-mono bg-slate-950 rounded-lg p-3 sm:p-4 overflow-x-auto"><code
					><span class="text-pink-400">import</span> <span class="text-slate-400">{'{'}</span> <span
						class="text-slate-300">AmetaClient</span
					> <span class="text-slate-400">{'}'}</span> <span class="text-pink-400">from</span> <span
						class="text-green-400">'@ameta/sdk'</span
					><span class="text-slate-400">;</span>

<span class="text-pink-400">const</span> <span class="text-slate-300">client</span> <span
						class="text-pink-400">=</span
					> <span class="text-pink-400">new</span> <span class="text-cyan-400">AmetaClient</span
					><span class="text-slate-400">(</span><span class="text-slate-400">{'{'}</span>
  <span class="text-slate-300">apiKey</span><span class="text-slate-400">:</span> <span
						class="text-slate-300">process</span
					><span class="text-slate-400">.</span><span class="text-slate-300">env</span><span
						class="text-slate-400">.</span
					><span class="text-slate-300">AMETA_API_KEY</span>
<span class="text-slate-400">{'}'})</span><span class="text-slate-400">;</span>

<span class="text-slate-500">// Get asset health</span>
<span class="text-pink-400">const</span> <span class="text-slate-300">health</span> <span
						class="text-pink-400">=</span
					> <span class="text-pink-400">await</span> <span class="text-slate-300">client</span><span
						class="text-slate-400">.</span
					><span class="text-slate-300">predictions</span><span class="text-slate-400">.</span><span
						class="text-cyan-400">getHealth</span
					><span class="text-slate-400">(</span><span class="text-green-400">'pump-001'</span><span
						class="text-slate-400">)</span
					><span class="text-slate-400">;</span>
<span class="text-slate-300">console</span><span class="text-slate-400">.</span><span
						class="text-cyan-400">log</span
					><span class="text-slate-400">(</span><span class="text-green-400">`Health: </span><span
						class="text-slate-400">${'${'}</span
					><span class="text-slate-300">health</span><span class="text-slate-400">.</span><span
						class="text-slate-300">score</span
					><span class="text-slate-400">{'}'}</span><span class="text-green-400">/100`</span><span
						class="text-slate-400">)</span
					><span class="text-slate-400">;</span>

<span class="text-slate-500">// Subscribe to alerts</span>
<span class="text-slate-300">client</span><span class="text-slate-400">.</span><span
						class="text-slate-300">alerts</span
					><span class="text-slate-400">.</span><span class="text-cyan-400">subscribe</span><span
						class="text-slate-400">(</span
					><span class="text-green-400">'pump-001'</span><span class="text-slate-400">,</span> <span
						class="text-slate-400">(</span
					><span class="text-slate-300">alert</span><span class="text-slate-400">)</span> <span
						class="text-pink-400">=></span
					> <span class="text-slate-400">{'{'}</span>
  <span class="text-slate-300">console</span><span class="text-slate-400">.</span><span
						class="text-cyan-400">log</span
					><span class="text-slate-400">(</span><span class="text-green-400">`Alert: </span><span
						class="text-slate-400">${'${'}</span
					><span class="text-slate-300">alert</span><span class="text-slate-400">.</span><span
						class="text-slate-300">message</span
					><span class="text-slate-400">{'}'}</span><span class="text-green-400">`</span><span
						class="text-slate-400">)</span
					><span class="text-slate-400">;</span>
  <span class="text-slate-300">slack</span><span class="text-slate-400">.</span><span
						class="text-cyan-400">notify</span
					><span class="text-slate-400">(</span><span class="text-slate-300">alert</span><span
						class="text-slate-400">)</span
					><span class="text-slate-400">;</span>
<span class="text-slate-400">{'}'})</span><span class="text-slate-400">;</span></code
				></pre>
		{/if}
	</div>

	<!-- Footer -->
	<div class="api-footer px-3 sm:px-6 py-3 sm:py-4 bg-slate-800/30 border-t border-slate-800">
		<div class="flex items-center justify-between gap-2">
			<p class="text-[10px] sm:text-xs text-slate-500">Full API documentation available</p>
			<a
				href="/{lang}/analytics"
				class="text-[10px] sm:text-xs text-ameta-green hover:underline whitespace-nowrap"
				>View docs →</a
			>
		</div>
	</div>
</div>
